import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/toast';
import {
  Search,
  Store,
  Star,
  Download,
  Filter,
  Grid3X3,
  CheckCircle2,
  Crown,
  ChevronLeft,
  ChevronRight,
  Send,
  MessageSquare,
  Sparkles,
  Image,
  X,
  Package,
  Server,
  Plus,
} from 'lucide-react';
import { Link } from 'react-router-dom';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface MarketplaceSkill {
  id: string;
  skill_id: string;
  name: string;
  description: string;
  long_description?: string;
  author: string;
  author_url?: string;
  version: string;
  category: string;
  tags?: string[];
  icon_url?: string;
  icon_emoji?: string;
  is_verified: boolean;
  is_featured: boolean;
  is_premium: boolean;
  price_cents: number;
  download_count: number;
  avg_rating: number;
  review_count: number;
  published_at?: string;
  created_at: string;
  recent_reviews?: Review[];
}

interface Review {
  id?: string;
  rating: number;
  title?: string;
  body?: string;
  is_verified_purchase?: boolean;
  helpful_count?: number;
  created_at: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const CATEGORIES = [
  { key: 'all', label: 'All', labelJa: 'すべて' },
  { key: 'productivity', label: 'Productivity', labelJa: '生産性' },
  { key: 'utility', label: 'Utility', labelJa: 'ユーティリティ' },
  { key: 'communication', label: 'Communication', labelJa: 'コミュニケーション' },
  { key: 'development', label: 'Development', labelJa: '開発' },
  { key: 'media', label: 'Media', labelJa: 'メディア' },
  { key: 'enterprise', label: 'Enterprise', labelJa: 'エンタープライズ' },
  { key: 'integration', label: 'Integration', labelJa: 'インテグレーション' },
];

const FEATURED_GRADIENTS = [
  'from-emerald-500 to-teal-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-sky-500 to-blue-600',
  'from-rose-500 to-pink-600',
  'from-lime-500 to-green-600',
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDownloads(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}k`;
  return count.toString();
}

function formatPrice(cents: number, t: (key: string) => string): string {
  if (!cents || cents === 0) return t('marketplace.free');
  return `$${(cents / 100).toFixed(2)}`;
}

function renderStars(rating: number) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="flex items-center gap-0.5">
      {Array.from({ length: full }).map((_, i) => (
        <Star key={`f${i}`} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
      ))}
      {half && (
        <span className="relative h-3.5 w-3.5">
          <Star className="absolute h-3.5 w-3.5 text-amber-400" />
          <span className="absolute inset-0 overflow-hidden w-1/2">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
          </span>
        </span>
      )}
      {Array.from({ length: empty }).map((_, i) => (
        <Star key={`e${i}`} className="h-3.5 w-3.5 text-muted-foreground/30" />
      ))}
    </span>
  );
}

function timeAgo(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days < 1) return t('marketplace.timeAgo.today');
  if (days < 7) return t('marketplace.timeAgo.daysAgo', { count: days });
  if (days < 30) return t('marketplace.timeAgo.weeksAgo', { count: Math.floor(days / 7) });
  if (days < 365) return t('marketplace.timeAgo.monthsAgo', { count: Math.floor(days / 30) });
  return t('marketplace.timeAgo.yearsAgo', { count: Math.floor(days / 365) });
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function Marketplace() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /* --- local state --- */
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [installTargetSkill, setInstallTargetSkill] = useState<MarketplaceSkill | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>('');

  /* --- submit form state --- */
  const [submitForm, setSubmitForm] = useState({
    skillId: '',
    name: '',
    description: '',
    longDescription: '',
    category: 'utility',
    version: '1.0.0',
    iconEmoji: '',
    repositoryUrl: '',
    documentationUrl: '',
  });

  /* --- review form state --- */
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    body: '',
  });

  /* ---------------------------------------------------------------- */
  /*  Queries                                                          */
  /* ---------------------------------------------------------------- */

  const { data: browseData, isLoading: browseLoading } = useQuery({
    queryKey: ['marketplace', 'browse', search, category, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (category !== 'all') params.set('category', category);
      params.set('page', page.toString());
      params.set('limit', '24');
      const { data } = await api.get(`/marketplace?${params.toString()}`);
      return data;
    },
  });

  const skills: MarketplaceSkill[] = browseData?.data || [];
  const pagination = browseData?.pagination || { page: 1, total: 0, totalPages: 1 };

  const { data: featuredSkills, isLoading: featuredLoading } = useQuery({
    queryKey: ['marketplace', 'featured'],
    queryFn: async () => {
      const { data } = await api.get('/marketplace/featured');
      return data as MarketplaceSkill[];
    },
  });

  const { data: skillDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['marketplace', 'detail', selectedSkillId],
    queryFn: async () => {
      const { data } = await api.get(`/marketplace/${selectedSkillId}`);
      return data as MarketplaceSkill;
    },
    enabled: !!selectedSkillId,
  });

  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ['marketplace', 'reviews', selectedSkillId],
    queryFn: async () => {
      const { data } = await api.get(`/marketplace/${selectedSkillId}/reviews?limit=10`);
      return data;
    },
    enabled: !!selectedSkillId,
  });

  const reviews: Review[] = reviewsData?.data || [];

  /* --- user instances (for instance-centric install) --- */
  const { data: instancesData } = useQuery({
    queryKey: ['instances'],
    queryFn: async () => {
      const { data } = await api.get('/instances?limit=100');
      return data;
    },
  });
  const instances = (instancesData?.instances || []).filter(
    (i: any) => i.status === 'running' || i.status === 'starting',
  );

  /* ---------------------------------------------------------------- */
  /*  Mutations                                                        */
  /* ---------------------------------------------------------------- */

  const submitSkillMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/marketplace/submit', submitForm);
      return data;
    },
    onSuccess: () => {
      showSuccess(t('marketplace.submitSuccessMsg'));
      setSubmitDialogOpen(false);
      setSubmitForm({
        skillId: '',
        name: '',
        description: '',
        longDescription: '',
        category: 'utility',
        version: '1.0.0',
        iconEmoji: '',
        repositoryUrl: '',
        documentationUrl: '',
      });
      queryClient.invalidateQueries({ queryKey: ['marketplace'] });
    },
    onError: () => {
      showError(t('marketplace.submitErrorMsg'));
    },
  });

  const submitReviewMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/marketplace/${selectedSkillId}/reviews`, reviewForm);
      return data;
    },
    onSuccess: () => {
      showSuccess(t('marketplace.reviewSuccessMsg'));
      setReviewDialogOpen(false);
      setReviewForm({ rating: 5, title: '', body: '' });
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'reviews', selectedSkillId] });
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'detail', selectedSkillId] });
    },
    onError: () => {
      showError(t('marketplace.reviewErrorMsg'));
    },
  });

  const installMutation = useMutation({
    mutationFn: async ({ instanceId, skill }: { instanceId: string; skill: MarketplaceSkill }) => {
      const { data } = await api.post(`/instances/${instanceId}/skills/install`, {
        skillId: skill.skill_id,
        name: skill.name,
        version: skill.version,
        author: skill.author,
        description: skill.description,
        category: skill.category,
      });
      // Track download count in background
      api.post(`/marketplace/${skill.id}/download`).catch(() => {});
      return data;
    },
    onSuccess: () => {
      showSuccess(t('marketplace.installSuccessMsg'));
      setInstallTargetSkill(null);
      setSelectedInstanceId('');
      queryClient.invalidateQueries({ queryKey: ['marketplace'] });
      queryClient.invalidateQueries({ queryKey: ['instance-skills'] });
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || t('marketplace.installErrorMsg'));
    },
  });

  /* ---------------------------------------------------------------- */
  /*  Handlers                                                         */
  /* ---------------------------------------------------------------- */

  const handleCategoryChange = (key: string) => {
    setCategory(key);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleOpenDetail = (skill: MarketplaceSkill) => {
    setSelectedSkillId(skill.id);
  };

  const handleCloseDetail = () => {
    setSelectedSkillId(null);
  };

  const handleInstall = (skill: MarketplaceSkill) => {
    setInstallTargetSkill(skill);
    setSelectedInstanceId('');
  };

  const handleConfirmInstall = () => {
    if (!selectedInstanceId || !installTargetSkill) return;
    installMutation.mutate({ instanceId: selectedInstanceId, skill: installTargetSkill });
  };

  /* ---------------------------------------------------------------- */
  /*  Featured carousel scroll                                         */
  /* ---------------------------------------------------------------- */

  const scrollFeatured = (direction: 'left' | 'right') => {
    const container = document.getElementById('featured-carousel');
    if (!container) return;
    const scrollAmount = 340;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  // No instances — show create prompt
  if (instancesData && instances.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Store className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {t('marketplace.title')}
            </h1>
          </div>
        </div>
        <Card className="border-dashed border-2 border-primary/20 bg-primary/5">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="h-20 w-20 rounded-full gradient-primary flex items-center justify-center mb-6">
              <Server className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">
              {t('marketplace.createInstanceFirst')}
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md text-center">
              {t('marketplace.createInstanceHint')}
            </p>
            <Link to="/dashboard/new">
              <Button size="lg" className="gap-2">
                <Plus className="h-4 w-4" />
                {t('marketplace.createInstance')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ============================================================ */}
      {/*  HEADER                                                       */}
      {/* ============================================================ */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Store className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {t('marketplace.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('marketplace.discoverSubtitle')}
            </p>
          </div>
        </div>

        <Button
          variant="outline"
          className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-950 shrink-0"
          onClick={() => setSubmitDialogOpen(true)}
        >
          <Send className="h-4 w-4" />
          <span className="hidden sm:inline">
            {t('marketplace.submitSkill')}
          </span>
        </Button>
      </div>

      {/* ============================================================ */}
      {/*  FEATURED SKILLS                                              */}
      {/* ============================================================ */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <h2 className="text-lg font-semibold">
              {t('marketplace.featured')}
            </h2>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => scrollFeatured('left')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => scrollFeatured('right')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {featuredLoading ? (
          <div className="flex gap-4 overflow-hidden">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-48 min-w-[320px] bg-muted animate-pulse rounded-xl flex-shrink-0"
              />
            ))}
          </div>
        ) : featuredSkills && featuredSkills.length > 0 ? (
          <div
            id="featured-carousel"
            className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40 snap-x snap-mandatory"
          >
            {featuredSkills.map((skill, idx) => (
              <Card
                key={skill.id}
                className={cn(
                  'min-w-[320px] max-w-[360px] flex-shrink-0 cursor-pointer snap-start border-0 overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02]',
                )}
                onClick={() => handleOpenDetail(skill)}
              >
                <div
                  className={cn(
                    'bg-gradient-to-br p-5 text-white',
                    FEATURED_GRADIENTS[idx % FEATURED_GRADIENTS.length],
                  )}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="h-14 w-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl shadow-lg">
                      {skill.icon_emoji || '🧩'}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {skill.is_verified && (
                        <span className="flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 text-[11px] font-medium">
                          <CheckCircle2 className="h-3 w-3" />
                          {t('marketplace.verified')}
                        </span>
                      )}
                      {skill.is_premium && (
                        <span className="flex items-center gap-1 bg-amber-400/30 backdrop-blur-sm rounded-full px-2 py-0.5 text-[11px] font-medium">
                          <Crown className="h-3 w-3" />
                          {t('marketplace.premium')}
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="font-bold text-lg leading-tight mb-1">
                    {skill.name}
                  </h3>
                  <p className="text-white/80 text-sm line-clamp-2 leading-relaxed">
                    {skill.description}
                  </p>
                </div>
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Download className="h-3.5 w-3.5" />
                        {formatDownloads(skill.download_count)}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                        {(skill.avg_rating || 0).toFixed(1)}
                      </span>
                    </div>
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        !skill.price_cents || skill.price_cents === 0
                          ? 'text-emerald-600'
                          : 'text-foreground',
                      )}
                    >
                      {formatPrice(skill.price_cents, t)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-10">
              <Sparkles className="h-8 w-8 text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">
                {t('marketplace.noFeatured')}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Separator />

      {/* ============================================================ */}
      {/*  SEARCH BAR                                                   */}
      {/* ============================================================ */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('marketplace.search')}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-10 h-11"
        />
      </div>

      {/* ============================================================ */}
      {/*  CATEGORY FILTERS                                             */}
      {/* ============================================================ */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground mr-1" />
        {CATEGORIES.map((cat) => (
          <Button
            key={cat.key}
            variant={category === cat.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleCategoryChange(cat.key)}
            className={cn(
              'rounded-full transition-all',
              category === cat.key
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20'
                : 'hover:border-emerald-300 hover:text-emerald-600',
            )}
          >
            {t(`marketplace.categories.${cat.key}`)}
          </Button>
        ))}
      </div>

      <Separator />

      {/* ============================================================ */}
      {/*  BROWSE RESULTS / SKILL GRID                                  */}
      {/* ============================================================ */}
      {browseLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <div className="h-44 bg-muted animate-pulse rounded-lg" />
            </div>
          ))}
        </div>
      ) : skills.length === 0 ? (
        /* ---- Empty state ---- */
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-20">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Grid3X3 className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {t('marketplace.noSkillsFound')}
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
              {t('marketplace.noSkillsHint')}
            </p>
            {(search || category !== 'all') && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => {
                  setSearch('');
                  setCategory('all');
                  setPage(1);
                }}
              >
                <X className="h-3.5 w-3.5" />
                {t('marketplace.clearFilters')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ---- Results count ---- */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('marketplace.skillsFound', { count: pagination.total })}
            </p>
          </div>

          {/* ---- Grid ---- */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {skills.map((skill) => (
              <Card
                key={skill.id}
                className="group cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/5 hover:border-emerald-200 dark:hover:border-emerald-800"
                onClick={() => handleOpenDetail(skill)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-xl shadow-md shadow-emerald-500/20 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      {skill.icon_emoji || '🧩'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <CardTitle className="text-base font-bold leading-tight truncate">
                          {skill.name}
                        </CardTitle>
                        {skill.is_verified && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t('marketplace.by')} {skill.author}
                      </p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                    {skill.description}
                  </p>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="capitalize text-[11px]">
                      {t(`marketplace.categories.${skill.category}`, skill.category)}
                    </Badge>
                    {skill.is_premium && (
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 text-[11px] gap-1">
                        <Crown className="h-3 w-3" />
                        {t('marketplace.premium')}
                      </Badge>
                    )}
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Download className="h-3.5 w-3.5" />
                        {formatDownloads(skill.download_count)}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                        {(skill.avg_rating || 0).toFixed(1)}
                      </span>
                    </div>
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        !skill.price_cents || skill.price_cents === 0
                          ? 'text-emerald-600'
                          : 'text-foreground',
                      )}
                    >
                      {formatPrice(skill.price_cents, t)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ---- Pagination ---- */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                {t('marketplace.previous')}
              </Button>
              <span className="text-sm text-muted-foreground px-3">
                {t('marketplace.pageOf', { page, totalPages: pagination.totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="gap-1"
              >
                {t('marketplace.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* ============================================================ */}
      {/*  SKILL DETAIL DIALOG                                          */}
      {/* ============================================================ */}
      <Dialog open={!!selectedSkillId} onOpenChange={(open) => !open && handleCloseDetail()}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailLoading ? (
            <div className="space-y-4 py-6">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-xl bg-muted animate-pulse" />
                <div className="space-y-2 flex-1">
                  <div className="h-6 w-48 bg-muted animate-pulse rounded" />
                  <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                </div>
              </div>
              <div className="h-20 bg-muted animate-pulse rounded" />
              <div className="h-12 bg-muted animate-pulse rounded" />
            </div>
          ) : skillDetail ? (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4 mb-2">
                  <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-3xl shadow-lg shadow-emerald-500/20 flex-shrink-0">
                    {skillDetail.icon_emoji || '🧩'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <DialogTitle className="text-xl">
                        {skillDetail.name}
                      </DialogTitle>
                      {skillDetail.is_verified && (
                        <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 dark:bg-emerald-950 rounded-full px-2 py-0.5 text-[11px] font-medium">
                          <CheckCircle2 className="h-3 w-3" />
                          {t('marketplace.verified')}
                        </span>
                      )}
                      {skillDetail.is_premium && (
                        <span className="flex items-center gap-1 text-amber-600 bg-amber-50 dark:bg-amber-950 rounded-full px-2 py-0.5 text-[11px] font-medium">
                          <Crown className="h-3 w-3" />
                          {t('marketplace.premium')}
                        </span>
                      )}
                    </div>
                    <DialogDescription className="mt-1">
                      v{skillDetail.version} &middot; {t('marketplace.by')}{' '}
                      {skillDetail.author_url ? (
                        <a
                          href={skillDetail.author_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {skillDetail.author}
                        </a>
                      ) : (
                        skillDetail.author
                      )}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Separator />

              <div className="space-y-5">
                {/* ---- Description ---- */}
                <div>
                  <h4 className="text-sm font-medium mb-1.5">
                    {t('marketplace.description')}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                    {skillDetail.long_description || skillDetail.description}
                  </p>
                </div>

                {/* ---- Tags & category ---- */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="capitalize">
                    {t(`marketplace.categories.${skillDetail.category}`, skillDetail.category)}
                  </Badge>
                  {skillDetail.tags?.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>

                {/* ---- Stats row ---- */}
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Download className="h-4 w-4" />
                    <span>
                      {formatDownloads(skillDetail.download_count)}{' '}
                      {t('marketplace.downloads')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {renderStars(skillDetail.avg_rating || 0)}
                    <span>
                      {(skillDetail.avg_rating || 0).toFixed(1)} ({t('marketplace.reviewCount', { count: skillDetail.review_count })})
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <span
                      className={cn(
                        'font-semibold',
                        !skillDetail.price_cents || skillDetail.price_cents === 0
                          ? 'text-emerald-600'
                          : 'text-foreground',
                      )}
                    >
                      {formatPrice(skillDetail.price_cents, t)}
                    </span>
                  </div>
                </div>

                {/* ---- Screenshots placeholder ---- */}
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    {t('marketplace.screenshots')}
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="aspect-video bg-muted rounded-lg border border-dashed border-muted-foreground/20 flex items-center justify-center"
                      >
                        <Image className="h-6 w-6 text-muted-foreground/30" />
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* ---- Reviews section ---- */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      {t('marketplace.reviews')}
                      {skillDetail.review_count > 0 && (
                        <span className="text-muted-foreground font-normal">
                          ({skillDetail.review_count})
                        </span>
                      )}
                    </h4>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-xs"
                      onClick={() => setReviewDialogOpen(true)}
                    >
                      <Star className="h-3.5 w-3.5" />
                      {t('marketplace.writeReview')}
                    </Button>
                  </div>

                  {reviewsLoading ? (
                    <div className="space-y-3">
                      {[1, 2].map((i) => (
                        <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
                      ))}
                    </div>
                  ) : reviews.length > 0 ? (
                    <div className="space-y-3">
                      {reviews.map((review, idx) => (
                        <Card key={review.id || idx} className="border-muted">
                          <CardContent className="py-3 px-4 space-y-1.5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {renderStars(review.rating)}
                                {review.title && (
                                  <span className="text-sm font-medium">{review.title}</span>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {timeAgo(review.created_at, t)}
                              </span>
                            </div>
                            {review.body && (
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                {review.body}
                              </p>
                            )}
                            {review.is_verified_purchase && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
                                <CheckCircle2 className="h-3 w-3" />
                                {t('marketplace.verifiedPurchase')}
                              </span>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-dashed">
                      <CardContent className="flex flex-col items-center justify-center py-8">
                        <MessageSquare className="h-8 w-8 text-muted-foreground/30 mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {t('marketplace.noReviewsHint')}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <Separator />

                {/* ---- Install button ---- */}
                <Button
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
                  onClick={() => handleInstall(skillDetail)}
                >
                  <Download className="h-4 w-4" />
                  {t('marketplace.installOnInstance')}
                  {skillDetail.price_cents > 0 && (
                    <span className="ml-1">
                      &middot; {formatPrice(skillDetail.price_cents, t)}
                    </span>
                  )}
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/*  INSTANCE SELECTOR DIALOG (for install)                       */}
      {/* ============================================================ */}
      <Dialog open={!!installTargetSkill} onOpenChange={(open) => { if (!open) { setInstallTargetSkill(null); setSelectedInstanceId(''); } }}>
        <DialogContent className="sm:max-w-lg">
          {installTargetSkill && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4 mb-2">
                  <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-2xl shadow-lg shadow-emerald-500/20 flex-shrink-0">
                    {installTargetSkill.icon_emoji || '🧩'}
                  </div>
                  <div>
                    <DialogTitle className="text-xl">
                      {t('marketplace.selectInstallTarget')}
                    </DialogTitle>
                    <DialogDescription className="mt-0.5">
                      {installTargetSkill.name} v{installTargetSkill.version}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Separator />

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    {t('marketplace.selectInstance')}
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {instances.map((instance: any) => (
                      <button
                        key={instance.id}
                        onClick={() => setSelectedInstanceId(instance.id)}
                        className={cn(
                          'w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all',
                          selectedInstanceId === instance.id
                            ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30 ring-1 ring-emerald-500/20'
                            : 'hover:bg-accent',
                        )}
                      >
                        <Server className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{instance.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{instance.hosting_type}</p>
                        </div>
                        {selectedInstanceId === instance.id && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
                  onClick={handleConfirmInstall}
                  disabled={!selectedInstanceId || installMutation.isPending}
                >
                  <Download className="h-4 w-4" />
                  {installMutation.isPending
                    ? t('marketplace.installing')
                    : t('marketplace.installOnInstance')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/*  WRITE REVIEW DIALOG                                          */}
      {/* ============================================================ */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('marketplace.writeReviewTitle')}</DialogTitle>
            <DialogDescription>
              {t('marketplace.shareExperience')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Rating */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.rating')}
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewForm((f) => ({ ...f, rating: star }))}
                    className="p-0.5 transition-transform hover:scale-125"
                  >
                    <Star
                      className={cn(
                        'h-6 w-6 transition-colors',
                        star <= reviewForm.rating
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-muted-foreground/30',
                      )}
                    />
                  </button>
                ))}
                <span className="text-sm text-muted-foreground ml-2">
                  {reviewForm.rating}/5
                </span>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.reviewTitleOptional')}
              </label>
              <Input
                placeholder={t('marketplace.reviewTitlePlaceholder')}
                value={reviewForm.title}
                onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.commentOptional')}
              </label>
              <textarea
                placeholder={t('marketplace.commentPlaceholder')}
                value={reviewForm.body}
                onChange={(e) => setReviewForm((f) => ({ ...f, body: e.target.value }))}
                rows={4}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => submitReviewMutation.mutate()}
                disabled={submitReviewMutation.isPending}
              >
                <Send className="h-4 w-4" />
                {submitReviewMutation.isPending
                  ? t('marketplace.submitting')
                  : t('marketplace.submitReview')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/*  SUBMIT SKILL DIALOG                                          */}
      {/* ============================================================ */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-500" />
              {t('marketplace.submitSkillTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('marketplace.submitSkillDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Skill ID */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.skillId')} <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="my-awesome-skill"
                value={submitForm.skillId}
                onChange={(e) => setSubmitForm((f) => ({ ...f, skillId: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                {t('marketplace.skillIdHint')}
              </p>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.skillName')} <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder={t('marketplace.skillNamePlaceholder')}
                value={submitForm.name}
                onChange={(e) => setSubmitForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            {/* Icon Emoji */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.iconEmoji')}
              </label>
              <Input
                placeholder="🧩"
                value={submitForm.iconEmoji}
                onChange={(e) => setSubmitForm((f) => ({ ...f, iconEmoji: e.target.value }))}
                className="w-24"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.shortDescription')} <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder={t('marketplace.shortDescriptionPlaceholder')}
                value={submitForm.description}
                onChange={(e) => setSubmitForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>

            {/* Long description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.longDescription')}
              </label>
              <textarea
                placeholder={t('marketplace.longDescriptionPlaceholder')}
                value={submitForm.longDescription}
                onChange={(e) =>
                  setSubmitForm((f) => ({ ...f, longDescription: e.target.value }))
                }
                rows={4}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.category')} <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.filter((c) => c.key !== 'all').map((cat) => (
                  <Button
                    key={cat.key}
                    type="button"
                    variant={submitForm.category === cat.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSubmitForm((f) => ({ ...f, category: cat.key }))}
                    className={cn(
                      'rounded-full text-xs',
                      submitForm.category === cat.key
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : '',
                    )}
                  >
                    {t(`marketplace.categories.${cat.key}`)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Version */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.version')}
              </label>
              <Input
                placeholder="1.0.0"
                value={submitForm.version}
                onChange={(e) => setSubmitForm((f) => ({ ...f, version: e.target.value }))}
                className="w-32"
              />
            </div>

            {/* Repository URL */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.repositoryUrl')}
              </label>
              <Input
                placeholder="https://github.com/user/skill"
                value={submitForm.repositoryUrl}
                onChange={(e) =>
                  setSubmitForm((f) => ({ ...f, repositoryUrl: e.target.value }))
                }
              />
            </div>

            {/* Documentation URL */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('marketplace.documentationUrl')}
              </label>
              <Input
                placeholder="https://docs.example.com"
                value={submitForm.documentationUrl}
                onChange={(e) =>
                  setSubmitForm((f) => ({ ...f, documentationUrl: e.target.value }))
                }
              />
            </div>

            <Separator />

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setSubmitDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => submitSkillMutation.mutate()}
                disabled={
                  submitSkillMutation.isPending ||
                  !submitForm.skillId ||
                  !submitForm.name ||
                  !submitForm.description
                }
              >
                <Send className="h-4 w-4" />
                {submitSkillMutation.isPending
                  ? t('marketplace.submitting')
                  : t('marketplace.submitForReview')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
